import { ProtectedRoute } from '@/components/auth/protected-route.js';
import {
  ExportsPage,
  type ExportsSearchParams,
} from '@/features/exports/components/exports-page.js';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

const exportsSearchSchema = z.object({
  status: z.enum(['queued', 'running', 'completed', 'failed', 'cancelled']).optional(),
  format: z.enum(['csv', 'xlsx', 'pdf']).optional(),
  pageNumber: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().optional(),
});

export const Route = createFileRoute('/exports')({
  validateSearch: (search) => exportsSearchSchema.parse(search),
  component: ExportsRoute,
});

function buildSearchParams(raw: z.infer<typeof exportsSearchSchema>): ExportsSearchParams {
  const out: ExportsSearchParams = {};
  if (raw.status !== undefined) out.status = raw.status;
  if (raw.format !== undefined) out.format = raw.format;
  if (raw.pageNumber !== undefined) out.pageNumber = raw.pageNumber;
  if (raw.pageSize !== undefined) out.pageSize = raw.pageSize;
  return out;
}

function ExportsRoute() {
  const rawSearch = Route.useSearch();
  const search = buildSearchParams(rawSearch);
  const navigate = useNavigate({ from: '/exports' });

  function handleSearchParamsChange(next: ExportsSearchParams) {
    const searchObj: Record<string, unknown> = {};
    if (next.status !== undefined) searchObj.status = next.status;
    if (next.format !== undefined) searchObj.format = next.format;
    if (next.pageNumber !== undefined && next.pageNumber > 1)
      searchObj.pageNumber = next.pageNumber;
    if (next.pageSize !== undefined) searchObj.pageSize = next.pageSize;

    void navigate({
      search: searchObj as z.infer<typeof exportsSearchSchema>,
      replace: true,
    });
  }

  return (
    <ProtectedRoute>
      <ExportsPage searchParams={search} onSearchParamsChange={handleSearchParamsChange} />
    </ProtectedRoute>
  );
}
